<template>
    <div class="flex flex-wrap px-0 py-0 lg:px-4 lg:py-5 bg-white gap-y-2">
        <!-- First Name -->
        <div class="w-full lg:w-1/2 lg:pr-4">
            <floating-input     v-model:value="firstName"
                                id="firstName"
                                label="First Name"
                                :readonly="! permissions.can('edit_name')"
            ></floating-input>
        </div>

        <!-- Last Name -->
        <div class="w-full lg:w-1/2 lg:pl-4">
            <floating-input     v-model:value="lastName"
                                id="lastName"
                                label="Last Name"
                                :readonly="! permissions.can('edit_name')"
            ></floating-input>
        </div>

        <!-- Email -->
        <div class="w-full lg:w-1/2 lg:pr-4">
            <floating-input     v-model:value="email"
                                id="email"
                                label="Email"
                                :readonly="true"
                                :errorMessage="validationErrors.emailMessage"
                                :hasError="validationErrors.email"
                                :required="true"
            ></floating-input>
        </div>

        <!-- Phone Number -->
        <div class="w-full lg:w-1/2 lg:pl-4">
            <floating-input     v-model:value="phoneNumber"
                                id="phoneNumber"
                                label="Phone Number (XXXXXXXXXX)"
                                :readonly="! permissions.can('edit_phone_number')"
                                :errorMessage="validationErrors.phoneNumberMessage"
                                :hasError="validationErrors.phoneNumber"
            ></floating-input>
        </div>

        <!-- Password Reset Email -->
        <div    v-if="permissions.can('send_password_resets')"
                class="w-full">
            <div    v-if="ssoEnabled">
                <banner     bg-color="bg-blue-400"
                            fg-color="text-white"
                >
                    <template   #banner-container>
                                                <span   class="text-white">
                                                    User has SSO Enabled.
                                                </span>
                    </template>
                </banner>
            </div>
            <button-blue-secondary  v-else
                                    :class="{ 'opacity-50 cursor-not-allowed': resettingPassword }"
                                    @click="sendResetPasswordEmail"
            >
                <span v-if="resettingPassword" class="flex items-center gap-2">
                    <span class="lnr icon-loading animate-spin" style="font-size: 1.2rem;"></span>
                    Sending Password Reset Email
                </span>
                <span v-else-if="errorResettingPassword === null">
                    Send Password Reset Email
                </span>
                <span v-else class="text-red-500">
                    {{ errorResettingPassword }}
                </span>
            </button-blue-secondary>
        </div>

      <!-- Change User Email -->
      <div class="w-full">
        <button-blue-secondary @click="navigateToUserEmailChangePage">
          <span>
            Change User's Primary Email Address
          </span>
        </button-blue-secondary>
      </div>

      <!-- Company Id -->
        <div    v-show="permissions.can('choose_user_company')"
                class="flex flex-col w-full mt-6 pt-4 border-t border-gray-100">
            <h5 class="text-left text-sm font-semibold text-gray-800 mb-2">User Company</h5>
            <select2
                :close-on-select="true"
                :options="companyList"
                v-model:value="companyId"
            ></select2>
        </div>

      <div v-if="initialAdminTrainingCompletedAt == null" class="w-full mt-6 pt-4 border-t border-gray-100">
        <h5 class="text-left text-sm font-semibold text-gray-800 mb-3">Training</h5>
        <div class="flex items-start">
          <input id="certify-training" aria-describedby="certify-training"
                 v-model="certify"
                 name="certify-training" type="checkbox" class="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600">
          <div class="ml-3 text-sm leading-6">
            <label for="certify-training" class="font-medium text-gray-900">Certify Training</label>
            <p id="comments-description" class="text-gray-500">Certify {{ user.display_name }} as having completed training today.</p>
          </div>
        </div>
      </div>

      <div v-else
           class="rounded-md bg-green-50 p-4 w-full mt-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-green-800">Training Certified on {{ initialAdminTrainingCompletedAt }}
              <span v-if="initialAdminTrainingRecordedByUser.display_name ?? null">
                by {{ initialAdminTrainingRecordedByUser.display_name}}
              </span>
            </p>
          </div>
        </div>
      </div>

    </div>
</template>

<script>
import axios from "axios"
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import Banner from "@/components/Banners/Banner";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary";
import FloatingInput from "@/components/elements/FloatingInput";
import Select2 from "@/components/elements/select2"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";

export default {
    name: "UserEditPersonalInformation",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {Banner, ButtonBlueSecondary, FloatingInput, Select2},
    emits: ['update:save', 'update:errorSaving', 'password-reset-sent'],
    props: {
        user: Object,
        companyList: Object,
        save: {
            type: [Boolean, String],
            required: true,
        },
        errorSaving: {
            type: Boolean,
            default: false,
        },
        groups: {
            type: Array,
            default() {
                return null
            },
        },
        permissionLevel: {
            type: String,
            default: null,
        },
        isEditingSelf: {
            type: Boolean,
            default: false,
        },
        // Indicates whether company access should be created for the user if they do not already have it (during save)
        shouldCreateCompanyAccess: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {

            loading: false,

            // User Info
            firstName: this.user.first_name,
            lastName: this.user.last_name,
            email: this.user.email,
            phoneNumber: this.user.phone_number,
            initialAdminTrainingCompletedAt: this.user.initial_admin_training_completed_at,
            initialAdminTrainingRecordedByUser: this.user.initial_admin_training_recorded_by_user,
            certify: false,
            companyId: null,
            ssoEnabled: this.user.sso_enabled,

            resettingPassword: false,
            errorResettingPassword: null,
            errors: null,

            validationErrors: {
                firstName: false,
                firstNameMessage: '',
                lastName: false,
                lastNameMessage: '',
                email: false,
                emailMessage: '',
                phoneNumber: false,
                phoneNumberMessage: '',
            },
        }
    },
    created() {

    },
    mounted() {
        this.companyId = this.user.company_id
    },
    methods: {
        saveUser() {
            if (!this.canEditUser && !this.isEditingSelf) {
                this.$emit('update:save', 'Unable to Edit User.')
                return
            }

            this.clearValidationErrors()
            this.errors = null
            let params = {
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                phoneNumber: this.phoneNumber,
                companyId: this.companyId,
                certify: this.certify,
                shouldCreateCompanyAccess: this.shouldCreateCompanyAccess,
            }
            if (!_.isNil(this.permissionLevel)) {
                params.permissionLevel = this.permissionLevel
            }
            if (!_.isNil(this.groups)) {
                params.groups = this.groups
            }
            axios.put(this.company.buildCompanyApiInternalRoute(`users/${this.user.id}`),
                params
            ).then(response => {
                let data = response.data.data
                this.firstName = data.first_name
                this.lastName = data.last_name
                this.email = data.email
                this.phoneNumber = data.phone_number
              console.log(data);
                console.log(data.initial_admin_training_completed_at)
                console.log(data.initial_admin_training_recorded_by_user)
                this.initialAdminTrainingCompletedAt = data.initial_admin_training_completed_at;
                this.initialAdminTrainingRecordedByUser = data.initial_admin_training_recorded_by_user;

                this.$emit('update:save', false)
                this.$emit('update:errorSaving', false)
            }).catch(error => {
                console.log(`Failed to save user: ${error}`)
                console.log(error.response.data.errors)
                let errorOccurred = this.handleValidationError(error)
                this.$emit('update:save', false)
                this.$emit('update:errorSaving', errorOccurred)
            }).then(() => {

            })
        },
        clearValidationErrors() {
            this.$emit('update:errorSaving', false)
            this.validationErrors = {
                firstName: false,
                firstNameMessage: '',
                lastName: false,
                lastNameMessage: '',
                email: false,
                emailMessage: '',
                phoneNumber: false,
                phoneNumberMessage: '',
            }
        },
        handleValidationError(error) {
            let errorOccurred = false
            if (error.response && error.response.status === 422) {
                const errors = error.response.data.errors || {}

                if (errors.firstName) {
                    errorOccurred = true
                    this.validationErrors.firstName = true
                    this.validationErrors.firstNameMessage = errors.firstName[0] || 'First name is required'
                }

                if (errors.lastName) {
                    errorOccurred = true
                    this.validationErrors.lastName = true
                    this.validationErrors.lastNameMessage = errors.lastName[0] || 'Last name is required'
                }

                if (errors.email) {
                    errorOccurred = true
                    this.validationErrors.email = true
                    this.validationErrors.emailMessage = errors.email[0] || 'Valid email is required'
                }

                if (errors.phoneNumber) {
                    errorOccurred = true
                    this.validationErrors.phoneNumber = true
                    this.validationErrors.phoneNumberMessage = errors.phoneNumber[0] || 'Phone number is invalid'
                }
            }

            return errorOccurred
        },
        sendResetPasswordEmail() {
            if (this.resettingPassword || this.user.sso_enabled) {
                return
            }

            this.resettingPassword = true
            axios.post('/password/email', {
                email: this.email,
            }).then(response => {
                this.$emit('password-reset-sent', this.email)
            }).catch(error => {
                this.errorResettingPassword = 'An Error Occurred sending the Password Reset Email.'
                console.log('Failed to Send Password Reset Email')
                console.log(error)
            }).then(() => {
                this.resettingPassword = false
            })
        },
        navigateToUserEmailChangePage(){
            window.location.href = this.company.buildCompanyRoute(`users/${this.user.id}/email`)
        },
    },
    computed: {
        canEditUser() {
            return this.permissions.can('update_users')
        },
    },
    watch: {
        save(val, old) {
            if (val !== old && val === true) {
                console.log('Save User Personal Info')
                this.saveUser()
            }
        },
        firstName() {
            if (this.validationErrors.firstName) {
                this.validationErrors.firstName = false
                this.validationErrors.firstNameMessage = ''
            }
        },
        lastName() {
            if (this.validationErrors.lastName) {
                this.validationErrors.lastName = false
                this.validationErrors.lastNameMessage = ''
            }
        },
        email() {
            if (this.validationErrors.email) {
                this.validationErrors.email = false
                this.validationErrors.emailMessage = ''
            }
        },
        phoneNumber() {
            if (this.validationErrors.phoneNumber) {
                this.validationErrors.phoneNumber = false
                this.validationErrors.phoneNumberMessage = ''
            }
        }
    },
}
</script>

<style scoped>

</style>